import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

interface CompanyPANInputProps {
  // Controlled pattern (used by JourneyPage)
  value?: { company_pan_number?: string | null };
  onChange?: (val: { company_pan_number: string }) => void;
  disabled?: boolean;
  registerSubmit?: (handler: () => Promise<boolean>) => void;
  // Legacy pattern
  initialData?: { company_pan_number?: string };
  onDataChange?: (data: { company_pan_number: string }) => void;
  // Cross-component communication
  onGSTEnriched?: (gstNumber: string) => void;
}

const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

function validateCompanyPAN(value: string): string {
  const trimmed = (value || '').trim();
  if (!trimmed) return 'Business PAN is required';
  if (trimmed.length !== 10) return 'PAN must be exactly 10 characters';
  if (!PAN_REGEX.test(trimmed)) return 'Invalid PAN format (e.g. ABCDE1234F)';
  return '';
}

const CompanyPANInput: React.FC<CompanyPANInputProps> = ({
  value,
  onChange,
  disabled,
  registerSubmit,
  initialData,
  onDataChange,
  onGSTEnriched,
}) => {
  const companyPan = (value?.company_pan_number ?? initialData?.company_pan_number ?? '') || '';

  const [touched, setTouched] = useState(false);
  const [isEnriching, setIsEnriching] = useState(false);
  const lastEnrichedPAN = useRef<string>('');

  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    if (!registerSubmit) return;
    registerSubmit(async () => {
      setTouched(true);
      const pan = (valueRef.current?.company_pan_number ?? '') || '';
      return validateCompanyPAN(pan) === '';
    });
  }, [registerSubmit]);

  // DISABLED: Auto-enrich GST when valid PAN is entered
  // This enrichment is now handled by check_personal_pan_udyam API on Personal Info page
  // const enrichGSTFromPAN = useCallback(async (pan: string) => {
  //   if (!pan || pan.length !== 10 || !PAN_REGEX.test(pan)) return;
  //   if (pan === lastEnrichedPAN.current) return; // Already enriched this PAN
  //
  //   setIsEnriching(true);
  //   lastEnrichedPAN.current = pan;

  //   try {
  //     const response = await axios.post('/api/caprion/enrich-business-pan/', {
  //       business_pan: pan
  //     });

  //     if (response.data.success && response.data.gst_number) {
  //       const gstNumber = response.data.gst_number;
  //
  //       // Notify parent component (JourneyPage) to update GST field
  //       if (onGSTEnriched) {
  //         onGSTEnriched(gstNumber);
  //       }
  //
  //       toast.success('GST number auto-filled from PAN', {
  //         description: `Found GST: ${gstNumber}`,
  //         duration: 3000,
  //       });
  //     }
  //   } catch (error: any) {
  //     // Silently fail - GST enrichment is optional
  //     console.log('[PAN-to-GST] No GST found for PAN:', pan);
  //   } finally {
  //     setIsEnriching(false);
  //   }
  // }, [onGSTEnriched]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
    onChange?.({ company_pan_number: val });
    onDataChange?.({ company_pan_number: val });
  }, [onChange, onDataChange]);

  const handleBlur = useCallback(() => {
    setTouched(true);
    
    // DISABLED: GST enrichment now handled by check_personal_pan_udyam API on Personal Info page
    // if (companyPan && companyPan.length === 10 && PAN_REGEX.test(companyPan)) {
    //   enrichGSTFromPAN(companyPan);
    // }
  }, [companyPan]);

  const error = touched ? validateCompanyPAN(companyPan) : '';

  return (
    <div className="space-y-1.5">
      <Label htmlFor="company_pan_number" className="text-gray-500 font-medium">
        Business PAN <span className="text-red-500">*</span>
      </Label>
      <div className="relative">
        <Input
          id="company_pan_number"
          type="text"
          maxLength={10}
          value={companyPan}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="ABCDE1234F"
          disabled={disabled || isEnriching}
          className={`border-gray-200 focus:border-intellend-green focus:ring-intellend-green rounded-xl py-6 uppercase ${error ? 'border-red-400 focus:border-red-500' : ''}`}
        />
        {isEnriching && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <svg className="animate-spin h-4 w-4 text-intellend-green" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        )}
      </div>
      {error && (
        <p className="text-xs text-red-500 mt-0.5">{error}</p>
      )}
      {isEnriching && (
        <p className="text-xs text-intellend-green mt-0.5">Fetching GST details...</p>
      )}
    </div>
  );
};

export default CompanyPANInput;
